import { AppManager } from 'core/appManager';
import { MaterialsQuery, ProcessesQuery } from 'apps/rfq/gql';
import { JobSpec, Material, Process } from 'generated/typescript';
import { analytics } from 'core/analytics';

const doesTreeContainId = (
  nodes: any[],
  id: any,
  childrenKey: string,
  key = 'id',
): boolean => {
  if (!nodes) return false;

  return nodes.some(
    node =>
      node[key] === id ||
      doesTreeContainId(node[childrenKey], id, childrenKey, key),
  );
};

export class ValidateRFQInputManager extends AppManager {
  static registryName = 'ValidateRFQInputManager';

  constructor(parent: AppManager, init = {}, options = {}) {
    super(parent, init, options);
  }

  queryProcesses = (materialId: string): Promise<Process[]> => {
    const processesQuery = new ProcessesQuery(this, {
      variables: {
        processFilter: {
          materialId,
        },
      },
    });

    return new Promise(resolve => {
      processesQuery.process((processes: Process[]) => {
        if (processes) {
          resolve(processes);
          processesQuery.disposer();
        }
      });
      const handleQueryError = (error: Error) => {
        resolve([]);
        analytics.logError(error);
      };
      processesQuery.setOnSysError(handleQueryError);
      processesQuery.setOnUserError(handleQueryError);
      processesQuery.query();
    });
  };

  queryMaterials = (processId: string): Promise<Material[]> => {
    const materialsQuery = new MaterialsQuery(this, {
      variables: {
        materialFilter: {
          processId,
        },
      },
    });

    return new Promise(resolve => {
      materialsQuery.process((materials: Material[]) => {
        if (materials) {
          resolve(materials);
          materialsQuery.disposer();
        }
      });
      const handleQueryError = (error: Error) => {
        resolve([]);
        analytics.logError(error);
      };
      materialsQuery.setOnSysError(handleQueryError);
      materialsQuery.setOnUserError(handleQueryError);
      materialsQuery.query();
    });
  };

  isRfqInputValid = async (jobSpec: JobSpec) => {
    const { build } = jobSpec;

    const validMaterials = await this.queryMaterials(build.process.id);

    const validProcesses = await this.queryProcesses(build.material.id);

    const isMaterialValid = doesTreeContainId(
      validMaterials,
      build.material.id,
      'childMaterials',
    );

    const isProcessValid = doesTreeContainId(
      validProcesses,
      build.process.id,
      'childProcesses',
    );

    return isMaterialValid && isProcessValid;
  };
}
